import {
  Box,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { ArrowRightIcon, CloseIcon } from '@atgfr8/icons';
import { Button } from '@components/Button';
import { getDB } from '@database';
import { getLatestReleaseNotesApi } from '@store/Global/service';
import { ReleaseNoteType } from '@typeDefinitions/releaseNotesTypes';
import { formatDateString } from '@utils/dateFormat';
import { HTMLPreview } from '@views/releaseNotes/components/HTMLPreview';
import { isEmpty } from 'lodash';
import { createEffect, createSignal, Show } from 'solid-js';

export const ReleaseNotification = () => {
  const [release, setRelease] = createSignal<ReleaseNoteType | null>(null);
  const [anchorEl, setAnchorEl] = createSignal<HTMLElement | null>(null);

  const closeNotification = () => {
    localStorage.setItem('releaseNotificationClosed', 'true');
    setRelease(null);
  };

  const handleOpenPopover = (event: MouseEvent) => {
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  createEffect(async () => {
    const db = getDB();
    if (!db) return;
    const closed = localStorage.getItem('releaseNotificationClosed');
    const host = window.location.host;

    if (
      closed == null &&
      host.includes('atgfr8.com') &&
      !host.includes('beta')
    ) {
      const latestRelease = await getLatestReleaseNotesApi();
      if (!isEmpty(latestRelease)) {
        setRelease(latestRelease);
      }
    }
  });

  return (
    <Show when={release()}>
      <Box
        backgroundColor="#a5ffc0"
        class="border-b border-black px-2 py-1 relative"
      >
        <Typography
          variant="subtitle2"
          align="center"
          sxProps={{ fontWeight: 'bold' }}
        >
          New release scheduled for {formatDateString(release()?.releaseDate)} —{' '}
          {release()?.title}.{' '}
          <a
            href="#"
            onClick={handleOpenPopover}
            style={{
              'font-weight': 'bold',
              'font-size': 'inherit',
              'text-decoration': 'underline',
              color: 'blue',
            }}
          >
            View Release Notes
          </a>
        </Typography>

        <IconButton
          size="sm"
          onClick={closeNotification}
          dataTestId="release-notification-close"
          style={{
            position: 'absolute',
            right: '24px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <CloseIcon size={20} />
        </IconButton>

        <Popover
          open={!!anchorEl()}
          anchorEl={anchorEl()}
          onClose={handleClosePopover}
          dataTestId="release-notification-popover"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              width: '40%',
              'max-width': '90vw',
              'max-height': '70vh',
              'overflow-y': 'auto',
              padding: 0,
            },
          }}
        >
          <Box>
            <Box
              class="px-2 py-1"
              backgroundColor="#f7f7f7"
              style={{
                'border-bottom': '1px solid #eee',
              }}
            >
              <Typography variant="subtitle2" sxProps={{ fontWeight: 'bold' }}>
                {release()?.title}
              </Typography>
            </Box>

            <Stack spacing={4} p={4}>
              <Button
                label="Try it early!"
                size="sm"
                class="w-full"
                endIcon={<ArrowRightIcon size={20} />}
                onClick={() => window.open('https://beta.atgfr8.com', '_blank')}
                dataTestId="try-release-button"
              />
              <HTMLPreview htmlContent={release()?.notes ?? ''} />
            </Stack>
          </Box>
        </Popover>
      </Box>
    </Show>
  );
};
